import { Employee } from 'nomst/Stores/Decision/employee';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { AmountCode, AmountPayType, PayRangeDisplayType, CompaRatioDisplayFormat } from 'typings/enums';
import { Amount } from 'nomst/Stores/Decision/amount';
import AmountConvert from 'common/amountConvert';
import SafeMath from 'common/safeMath';
import { PayRangeConfiguration } from 'nomst/Stores/Configuration/payRangeConfiguration';
import { Format } from 'nomst/Stores/Utility/format';
import { StockAmount } from 'nomst/Stores/Decision/stockAmount';
import { Range } from 'nomst/Stores/Decision/range';

export default class CompViewHelper {
    static getPayRangeText(displayType: PayRangeDisplayType): string {
        return displayType === PayRangeDisplayType.CompaRatio ? 'Compa Ratio' : 'Range Penetration';
    }

    static getRangePenetration(payRange: Range, payAmount: number, format: Format): string {
        const payDifference = payAmount - payRange.minRange;
        const range = payRange.maxRange - payRange.minRange;
        const rangePenetrationPercentage =  SafeMath.divide(payDifference, range);
        return format.formatDecimalToPercentage(rangePenetrationPercentage);
    }

    static getCompaRatio(
        payRange: Range,
        payAmount: number,
        compaRatioDisplayFormat: CompaRatioDisplayFormat,
        format: Format): string {
        const compaRatio =  SafeMath.divide(payAmount, payRange.midRange);
        return compaRatioDisplayFormat === CompaRatioDisplayFormat.ShowAsPercentage
            ? format.formatDecimalToPercentage(compaRatio)
            : format.formatCompaRatioToAbsolute(compaRatio);
    }
    
    static getPayRangeValue(employee: Employee, payRangeConfig: PayRangeConfiguration, format: Format): string {
        if (!employee.range) {
            return '';
        }
        if (payRangeConfig.displayType === PayRangeDisplayType.RangePenetration) {
            return CompViewHelper.getRangePenetration(employee.range, employee.basePayForPayRange, format);
        } else {
            return CompViewHelper.getCompaRatio(
                employee.range,
                employee.basePayForPayRange,
                payRangeConfig.compaRatioDisplayFormat,
                format);
        }
    }
    
    static getAmountPercentage(amount: Amount, employee: Employee): number {
        return amount.payType !== AmountPayType.BasePay
            ? amount.getPercentageBasedOnAnnualPay(
                AmountConvert.convertToAnnual(
                    amount.currentPay,
                    employee.annualStandardWorkHours,
                    employee.isHourly,
                    employee.fullTimeEquivalent))
            : amount.percentage;
    }

    static shouldRenderAmount(amount: Amount, configuration: Configuration): boolean {
        return amount.amountCode !== AmountCode.Stock
            && configuration.teamPageViews.isAmountUsedInCompSummary(amount.amountCode);
    }
    
    static shouldTccIncludeAmount(amount: Amount, configuration: Configuration, doesAffectTcc: boolean): boolean {
        return !amount.updateNewPay
            && CompViewHelper.shouldRenderAmount(amount, configuration)
            && configuration.teamPageViews.doesAmountUseTccField(amount.amountCode) === doesAffectTcc;
    }
    
    static getNonCycleAmounts(
        employee: Employee,
        configuration: Configuration,
        doesAffectTcc: boolean): INonCycleAmount[] {
        const {
            isLastPaidStiUsedInCompSummary,
            doesLastPaidStiUseTccField,
            isCommissionsUsedInCompSummary,
            doesCommissionsUseTccField,
            isOtherCashUsedInCompSummary,
            doesOtherCashUseTccField
        } = configuration.teamPageViews;
    
        return [
            {
                className: 'last-paid-sti',
                title: 'Last Paid STI',
                amount: employee.convertedBonus,
                shouldRender: isLastPaidStiUsedInCompSummary && doesLastPaidStiUseTccField === doesAffectTcc
            },
            {
                className: 'other-cash',
                title: 'Other Cash',
                amount: employee.convertedTotalAnnualShortTermIncentives,
                shouldRender: isOtherCashUsedInCompSummary && doesOtherCashUseTccField === doesAffectTcc
            },
            {
                className: 'commissions',
                title: 'Commissions',
                amount: employee.convertedCommissionsPaid,
                shouldRender: isCommissionsUsedInCompSummary && doesCommissionsUseTccField === doesAffectTcc
            }
        ];
    }
    
    static getStockText(stockAmount: StockAmount, format: Format): string {
        if (!stockAmount || stockAmount.amount <= 0) {
            return '0';
        }

        const numberOfSharesText = format.formatStockDecimal(stockAmount.numberOfShares);
        if (stockAmount.isValueInUnit) {
            return numberOfSharesText;
        }

        const stockAmountText = format.formatCurrencyValue(
            stockAmount.currentIsoCurrencyCode,
            stockAmount.convertedMonetaryAmount || 0,
            false);
        return `${stockAmountText} / ${numberOfSharesText}`;
    }
}